import { useSelector } from "react-redux";
import { mainSplitApi } from "@/src-refactor/modules/store/qrtk/mainSplitApi";
import { RootState } from "@/src-refactor/modules/store/store";

type QueryStatus = "uninitialized" | "pending" | "fulfilled" | "rejected";

interface PropertyListQueryState {
  status: QueryStatus;
  isUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: unknown;
}

const DEFAULT_STATE: PropertyListQueryState = {
  status: "uninitialized",
  isUninitialized: true,
  isLoading: false,
  isFetching: false,
  isSuccess: false,
  isError: false
};

const STATUS_PRIORITY: Record<QueryStatus, number> = {
  uninitialized: 0,
  fulfilled: 1,
  pending: 2,
  rejected: 3
};

const normalizeStatus = (status?: string, isFetching?: boolean): QueryStatus => {
  if (status === "rejected") return "rejected";
  if (isFetching || status === "pending") return "pending";
  if (status === "fulfilled") return "fulfilled";
  return "uninitialized";
};

const selectPropertyListQueryState = (state: RootState): PropertyListQueryState => {
  const queries = state?.[mainSplitApi.reducerPath]?.queries;
  if (!queries) return DEFAULT_STATE;

  const propertyQueries = Object.values(queries).filter(entry => {
    if (!entry || typeof entry !== "object") return false;

    const query = entry as {
      endpointName?: string;
    };

    return query.endpointName === "getPropertyList";
  });

  if (propertyQueries.length === 0) {
    return DEFAULT_STATE;
  }

  return propertyQueries.reduce<PropertyListQueryState>((accumulator, entry) => {
    const query = entry as {
      status?: string;
      isFetching?: boolean;
      error?: unknown;
    };

    const normalizedStatus = normalizeStatus(query.status, query.isFetching);

    if (STATUS_PRIORITY[normalizedStatus] > STATUS_PRIORITY[accumulator.status]) {
      accumulator.status = normalizedStatus;
    }

    if (normalizedStatus !== "uninitialized") {
      accumulator.isUninitialized = false;
    }

    if (normalizedStatus === "pending") {
      accumulator.isLoading = true;
    }

    if (query.isFetching) {
      accumulator.isFetching = true;
    }

    if (normalizedStatus === "fulfilled") {
      accumulator.isSuccess = true;
    }

    if (normalizedStatus === "rejected") {
      accumulator.isError = true;
      if (accumulator.error === undefined) {
        accumulator.error = query.error;
      }
    }

    return accumulator;
  }, { ...DEFAULT_STATE });
};

const usePropertyListLoading = () => useSelector(selectPropertyListQueryState);

export type { PropertyListQueryState };
export default usePropertyListLoading;
